import { TZDate } from '@date-fns/tz';
import { addDays } from 'date-fns/addDays';
import { isSameYear } from 'date-fns/isSameYear';
import { isToday } from 'date-fns/isToday';
import { isTomorrow } from 'date-fns/isTomorrow';
import { isYesterday } from 'date-fns/isYesterday';
import { format } from 'date-fns/format';
import { addHours } from 'date-fns';

const SHORT_MONTHS = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июня',
  'июля',
  'авг',
  'сент',
  'окт',
  'нояб',
  'дек',
];
const SHORT_MONTHS_NOMINATIVE = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июнь',
  'июль',
  'авг',
  'сент',
  'окт',
  'нояб',
  'дек',
];
const FULL_MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];
const FULL_MONTHS_NOMINATIVE = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
]

const SHORT_WEEK_DAY = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const FULL_WEEK_DAY = [
  'воскресенье',
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
];

type FormatMonthOps = {
  month: 'full' | 'short';
  year?: 'show' | 'hide' | 'notCurrent';
  nominative?: boolean;
}
const DEFAULT_FORMAT_MONTH_OPS: FormatMonthOps = {
  month: 'full',
  year: 'hide',
  nominative: false,
}

export function formatMonthShort(date: Date, nominative: boolean = false) {
  if (nominative) {
    return SHORT_MONTHS_NOMINATIVE[date.getMonth()]!;
  }
  return SHORT_MONTHS[date.getMonth()]!;
}

export function formatMonthFull(date: Date, nominative: boolean = false): string {
  if (nominative) {
    return FULL_MONTHS_NOMINATIVE[date.getMonth()]!;
  }
  return FULL_MONTHS[date.getMonth()]!;
}

export function formatMonth(date: Date, _ops: Partial<FormatMonthOps>) {
  const ops = { DEFAULT_FORMAT_MONTH_OPS, ..._ops };

  let month = formatMonthFull(date, ops.nominative);
  if (ops.month === 'short') {
    month = formatMonthShort(date, ops.nominative)
  }

  if (ops.year === 'show' || (ops.year === 'notCurrent' && date.getFullYear() !== new Date().getFullYear())) {
    return `${month} ${date.getFullYear()}`
  }

  return month;
}

export function formatWeekDayShort(date: Date) {
  return SHORT_WEEK_DAY[date.getDay()];
}

export function formatWeekDayFull(date: Date) {
  return FULL_WEEK_DAY[date.getDay()];
}

export function formatDateRangeShort(start: Date, end: Date): string {
  const startDay = start.getDate().toString().padStart(2, '0');
  const startMonth = formatMonthShort(start);
  const startYear = start.getFullYear();
  const startStr = `${startDay} ${startMonth} ${startYear}`;

  const endDay = end.getDate().toString().padStart(2, '0');
  const endMonth = formatMonthShort(end);
  const endYear = end.getFullYear();
  const endStr = `${endDay} ${endMonth} ${endYear}`;

  if (startStr === endStr) {
    // совпадают
    return `${startDay} ${startMonth} ${startYear}`;
  } else if (startYear !== endYear) {
    // в разных годах
    return `${startDay} ${startMonth} ${startYear} — ${endDay} ${endMonth} ${endYear}`;
  } else if (startMonth !== endMonth) {
    // в разных месяцах
    return `${startDay} ${startMonth} — ${endDay} ${endMonth} ${endYear}`;
  } else {
    // в разных датах
    return `${startDay} — ${endDay} ${endMonth} ${endYear}`;
  }
}

export function formatDateRangeShortMultiline(start: Date, end: Date): string[] {
  const startDay = start.getDate().toString().padStart(2, '0');
  const startMonth = formatMonthShort(start);
  const startYear = start.getFullYear();
  const startStr = `${startDay} ${startMonth} ${startYear}`;

  const endDay = end.getDate().toString().padStart(2, '0');
  const endMonth = formatMonthShort(end);
  const endYear = end.getFullYear();
  const endStr = `${endDay} ${endMonth} ${endYear}`;

  if (startStr === endStr) {
    // совпадают
    return [`${startDay} ${startMonth} ${startYear}`];
  } else if (startYear !== endYear) {
    // в разных годах
    return [`${startDay} ${startMonth} ${startYear} —`, `${endDay} ${endMonth} ${endYear}`];
  } else if (startMonth !== endMonth) {
    // в разных месяцах
    return [`${startDay} ${startMonth} — ${endDay} ${endMonth}`, `${endYear}`];
  } else {
    // в разных датах
    return [`${startDay} — ${endDay}`, `${endMonth} ${endYear}`];
  }
}

const defaultOptions: FormatDateOptions = {
  humanReadableDay: true,
  showTime: false,
  shortMonth: false,
  year: 'show',
  timezone: 'current',
};

export type FormatDateOptions = {
  humanReadableDay?: boolean;
  showTime?: boolean;
  shortMonth?: boolean;
  year?: 'show' | 'hide' | 'notCurrent';
  forceLeadingZero?: boolean;
  timezone?: 'current' | 'utc';
};
export function formatDate(input: Date | number | string, opts?: FormatDateOptions) {
  const options = Object.assign({}, defaultOptions, opts);

  const date = opts?.timezone === 'utc' ? new TZDate(new Date(input), 'UTC') : new Date(input);

  const time = options.showTime ? `, ${formatTime(date)}` : '';

  if (options.humanReadableDay) {
    let day;
    if (isToday(date)) day = 'Сегодня';
    if (isTomorrow(date)) day = 'Завтра';
    if (isYesterday(date)) day = 'Вчера';

    if (day) return `${day}${time}`;
  }

  const now = new Date();

  let day = date.getDate().toString();
  if (day.length === 1 && options.forceLeadingZero) {
    day = '0' + day;
  }

  const month = options.shortMonth ? formatMonthShort(date) : formatMonthFull(date);

  let year = '';
  if (options.year === 'show' || (options.year === 'notCurrent' && !isSameYear(date, now))) {
    year = ` ${date.getFullYear()}`;
  }

  return `${day}\u00A0${month}${year}${time}`;
}

export function formatTime(input: Date | number) {
  const date = new Date(input);

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function isLater(input: Date | number) {
  const now = new Date();
  const target = new Date(input);

  return target > now;
}

export function isSoon(input: Date | number) {
  const soonDate = addHours(now(), 1);
  const target = new Date(input);

  return now() < target && target < soonDate;
}

export function now() {
  return new Date();
}

export function tomorrow() {
  return addDays(now(), 1);
}

export function yesterday() {
  return addDays(now(), -1);
}

// FIXME: продублировано из монорепы, нужно смёрджить с основным кодом

export type FormatDateOptions2 = {
  humanReadableDay?: boolean;
  showTime?: boolean;
  year?: 'show' | 'hide' | 'notCurrent';
  month?: 'full' | 'short' | 'hide';
  forceLeadingZero?: boolean;
  timezone?: 'current' | 'utc';
};
export function formatDate2(input: Date | number | string, opts?: FormatDateOptions2) {
  const options = Object.assign({}, defaultOptions, opts);

  const date = opts?.timezone === 'utc' ? new TZDate(new Date(input), 'UTC') : new Date(input);

  const time = options.showTime ? `, ${formatTime(date)}` : '';

  if (options.humanReadableDay) {
    let day;
    if (isToday(date)) day = 'Сегодня';
    if (isTomorrow(date)) day = 'Завтра';
    if (isYesterday(date)) day = 'Вчера';

    if (day) return `${day}${time}`;
  }

  const now = new Date();

  let day = date.getDate().toString();
  if (day.length === 1 && options.forceLeadingZero) {
    day = '0' + day;
  }

  let month = '';
  if (opts?.month === 'short') month = `\u00A0${formatMonthShort(date)}`;
  if (opts?.month === 'full') month = `\u00A0${formatMonthFull(date)}`;

  let year = '';
  if (options.year === 'show' || (options.year === 'notCurrent' && !isSameYear(date, now))) {
    year = `\u00A0${date.getFullYear()}`;
  }

  return `${day}${month}${year}${time}`;
}

export function formatTechDate(date?: Date) {
  if (!date) return date;
  return format(date, 'yyyy-MM-dd');
}
